import { createStore } from 'devextreme-aspnet-data-nojquery';
import { GetFormatedDateWithTime } from '../../../common/Utils';
import { IM } from '../../../const/constants';
import apiRoutes from '../../../assets/apiRoutes.json';

// All endpoints come from assets/apiRoutes.json (project convention) — no hardcoded route strings. The
// reference sub-tabs reuse the existing IM master endpoints; the reused OM grids use the OM SaleOrder block.
export const ROUTES = {
  TransactionHeaders: apiRoutes.IM.ItemManagement.GetTransactionHeaders,
  TransactionDetail: apiRoutes.IM.ItemManagement.GetTransactionDetail,
  ItemTransactions: apiRoutes.IM.ItemManagement.GetItemTransactions,
  ItemLots: apiRoutes.IM.ItemManagement.GetItemLots,
  OpenPos: apiRoutes.IM.ItemManagement.GetOpenPos,
  OmTransactions: apiRoutes.IM.ItemManagement.GetOmTransactions,
  LotLocations: apiRoutes.IM.ItemManagement.GetLotLocations,
  VendorCrossReference: apiRoutes.IM.ItemManagement.GetVendorCrossReference,
  Items: apiRoutes.IM.GetItems,
  Lots: apiRoutes.IM.Lot.GetLots,
  Locations: apiRoutes.IM.GetLocations,
  ItemMasterMain: apiRoutes.OM.SaleOrder.GetItemMasterMain,
};

const authHeader = (accessToken) => (method, ajaxOptions) => {
  ajaxOptions.headers = {
    access_token: accessToken,
    Authorization: `Bearer ${accessToken}`,
  };
};

// Top grid — Open (historical=false) / Historical (historical=true) transactions. Search-panel fields
// ride along as load params and are applied server-side (ItemTransactionFilter). `keyFilter` (optional)
// restricts to headers for a selected item/lot — used by the Historical Transactions bottom sub-tab.
export const GetTransactionHeaderGridData = (accessToken, historical, formFields, keyFilter) => {
  const f = formFields || {};
  const k = keyFilter || {};
  const loadParams = {
    historical: () => !!historical,
    ItemId: () => k.itemId || '',
    LotId: () => k.lotId || '',
    TransactionType: () => f.TransactionType || '',
    Stage: () => f.Stage || '',
    Status: () => f.Status || '',
    TransactionDateFrom: () => GetFormatedDateWithTime(f.TransactionDateFrom),
    TransactionDateTo: () => GetFormatedDateWithTime(f.TransactionDateTo),
    Reference: () => f.Reference || '',
    Description: () => f.Description || '',
    FiscalYear: () => f.FiscalYear || '',
    FiscalPeriod: () => f.FiscalPeriod || '',
  };
  for (let i = 1; i <= 10; i++) {
    loadParams[`Code${i}`] = () => f[`Code${i}`] || '';
    loadParams[`Reference${i}`] = () => f[`Reference${i}`] || '';
  }

  return createStore({
    key: 'rowid',
    loadParams,
    loadUrl: `${window._env_.REACT_APP_APIS}/${ROUTES.TransactionHeaders}`,
    onBeforeSend: authHeader(accessToken),
  });
};

// Bottom "Detail" grid — the selected header's lines.
export const GetTransactionDetailGridData = (accessToken, headerId) =>
  createStore({
    key: 'rowid',
    loadParams: { headerId: () => headerId },
    loadUrl: `${window._env_.REACT_APP_APIS}/${ROUTES.TransactionDetail}`,
    onBeforeSend: authHeader(accessToken),
  });

// Item Master tab master grid — reuses the (IM-scoped) om/SaleOrder/GetItemMasterMain endpoint, but only
// sends the search fields this panel actually has (defaulted to '' / formatted date), so the numeric item
// fields the OM factory would send as `undefined` never reach the backend (they default server-side).
export const GetItemMasterMainStore = (accessToken, formFields) => {
  const f = formFields || {};
  const loadParams = {
    WarehouseID: () => f.WarehouseID || '',
    ItemCode: () => f.ItemCode || '',
    Description_1: () => f.Description_1 || '',
    Description_2: () => f.Description_2 || '',
    ClassCode: () => f.ClassCode || '',
    SubClassCode: () => f.SubClassCode || '',
    Status: () => f.Status || '',
    ItemType: () => f.ItemType || '',
    ItemSourceFlag: () => f.ItemSourceFlag || '',
    InventoryUnitUom: () => f.InventoryUnitUom || '',
    CareInstructions: () => f.CareInstructions || '',
    CreationUserId: () => f.CreationUserId || '',
    DateIntroduced: () => GetFormatedDateWithTime(f.DateIntroduced),
    DateAvailable: () => GetFormatedDateWithTime(f.DateAvailable),
    DateExpiration: () => GetFormatedDateWithTime(f.DateExpiration),
  };
  for (let i = 1; i <= 10; i++) {
    loadParams[`UserDefinedCode${i}`] = () => f[`UserDefinedCode${i}`] || '';
    loadParams[`UserDefinedReference${i}`] = () => f[`UserDefinedReference${i}`] || '';
  }
  return createStore({
    key: 'rowid',
    loadParams,
    loadUrl: `${window._env_.REACT_APP_APIS}/${ROUTES.ItemMasterMain}`,
    onBeforeSend: authHeader(accessToken),
  });
};

// "Item/Lot" tab master grid — one row per lot (IMLOT joined IMITEM). Sends only clean text params from the
// search panel (defaulted to '') so undefined values never reach the backend. Grid key = lot rowid.
export const GetItemLotMainStore = (accessToken, formFields) => {
  const f = formFields || {};
  const loadParams = {
    ItemCode: () => f.ItemCode || '',
    LotCode: () => f.LotCode || '',
    ItemDescription1: () => f.ItemDescription1 || '',
    LotDescription1: () => f.LotDescription1 || '',
    ItemClassCode: () => f.ItemClassCode || '',
    ItemSubClassCode: () => f.ItemSubClassCode || '',
    LotClassCode: () => f.LotClassCode || '',
    LotSubClassCode: () => f.LotSubClassCode || '',
    LotStatus: () => f.LotStatus || '',
    Warehouse: () => f.Warehouse || '',
  };
  for (let i = 1; i <= 10; i++) {
    loadParams[`ItemCode${i}`] = () => f[`ItemCode${i}`] || '';
    loadParams[`LotCode${i}`] = () => f[`LotCode${i}`] || '';
  }
  return createStore({
    key: 'rowid',
    loadParams,
    loadUrl: `${window._env_.REACT_APP_APIS}/${ROUTES.ItemLots}`,
    onBeforeSend: authHeader(accessToken),
  });
};

// Item/Lot sub-tabs keyed off the selected lot (IM-side lot-keyed endpoints, camelCase).
export const GetOpenPosGridData = (accessToken, lotId) =>
  createStore({
    key: 'rowid',
    loadParams: { lotId: () => lotId },
    loadUrl: `${window._env_.REACT_APP_APIS}/${ROUTES.OpenPos}`,
    onBeforeSend: authHeader(accessToken),
  });

export const GetOmTransactionsGridData = (accessToken, lotId) =>
  createStore({
    key: 'rowid',
    loadParams: { lotId: () => lotId },
    loadUrl: `${window._env_.REACT_APP_APIS}/${ROUTES.OmTransactions}`,
    onBeforeSend: authHeader(accessToken),
  });

export const GetLotLocationsGridData = (accessToken, lotId) =>
  createStore({
    key: 'rowid',
    loadParams: { lotId: () => lotId },
    loadUrl: `${window._env_.REACT_APP_APIS}/${ROUTES.LotLocations}`,
    onBeforeSend: authHeader(accessToken),
  });

// Reference sub-tabs — full IM master lists (Delphi: unfiltered, independent of the selected transaction).
export const GetItemMasterGridData = (accessToken) =>
  createStore({
    key: 'RowId',
    loadUrl: `${window._env_.REACT_APP_APIS}/${ROUTES.Items}`,
    onBeforeSend: authHeader(accessToken),
  });

export const GetItemLotGridData = (accessToken) =>
  createStore({
    key: 'RowId',
    loadUrl: `${window._env_.REACT_APP_APIS}/${ROUTES.Lots}`,
    onBeforeSend: authHeader(accessToken),
  });

export const GetLocationMasterGridData = (accessToken) =>
  createStore({
    key: 'RowId',
    loadParams: { Module: () => IM },
    loadUrl: `${window._env_.REACT_APP_APIS}/${ROUTES.Locations}`,
    onBeforeSend: authHeader(accessToken),
  });

// "Location Master" top-tab grid (Delphi frmIMM0051.qryIMLocation) — IMLOCATION for Module = IM, with the
// search-panel fields applied server-side (LocationDto). Grid key = RowId (PascalCase serialization).
export const GetLocationMasterMainStore = (accessToken, formFields) => {
  const f = formFields || {};
  return createStore({
    key: 'RowId',
    loadParams: {
      Module: () => IM,
      IdentityId: () => f.IdentityId || '',
      LocationCode: () => f.LocationCode || '',
      Description: () => f.Description || '',
      ClassCode: () => f.ClassCode || '',
      SubClassCode: () => f.SubClassCode || '',
      Status: () => f.Status || '',
      LocationType: () => f.LocationType || '',
      Priority: () => f.Priority || '',
    },
    loadUrl: `${window._env_.REACT_APP_APIS}/${ROUTES.Locations}`,
    onBeforeSend: authHeader(accessToken),
  });
};

// "Item Transactions" sub-tab (Delphi frmIMM0050) — transaction detail lines for the selected item
// (Item Master tab → { itemId }) or lot (Item/Lot tab → { lotId }). Same endpoint; lotId wins server-side.
export const GetItemTransactionsGridData = (accessToken, keyFilter) => {
  const k = keyFilter || {};
  return createStore({
    key: 'rowid',
    loadParams: { itemId: () => k.itemId || '', lotId: () => k.lotId || '' },
    loadUrl: `${window._env_.REACT_APP_APIS}/${ROUTES.ItemTransactions}`,
    onBeforeSend: authHeader(accessToken),
  });
};

// "Vendor Cross Reference" — full frmIMM0052 rows for the selected item ({ itemId }) or lot ({ lotId }).
// Same endpoint (IMVendorCrossReferenceDTO → key "rowid"); lotId wins server-side.
export const GetVendorCrossReferenceGridData = (accessToken, keyFilter) => {
  const k = keyFilter || {};
  return createStore({
    key: 'rowid',
    loadParams: { itemId: () => k.itemId || '', lotId: () => k.lotId || '' },
    loadUrl: `${window._env_.REACT_APP_APIS}/${ROUTES.VendorCrossReference}`,
    onBeforeSend: authHeader(accessToken),
  });
};
